import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, Lock, MessageCircle } from 'lucide-react';
import { rpcTriagem } from '@/components/kanban/triagem/rpcTriagem';
import { TriagemAtivacaoModal } from '@/components/kanban/ativacao/TriagemAtivacaoModal';
import { TriagemPortabilidadeModal } from '@/components/kanban/ativacao/TriagemPortabilidadeModal';
import { TriagemPortabilidadeConcluidaModal } from '@/components/kanban/ativacao/TriagemPortabilidadeConcluidaModal';
import { TriagemCpfDivergenteModal } from '@/components/kanban/ativacao/TriagemCpfDivergenteModal';
import { TriagemNotaFiscalModal } from '@/components/kanban/ativacao/TriagemNotaFiscalModal';
import { TriagemFluxoModal } from '@/components/kanban/fluxo/TriagemFluxoModal';
import { TRIAGENS_FLUXO } from '@/components/kanban/fluxo/triagensFluxoConfig';
import { useUserProfile } from '@/hooks/useUserProfile';
import { rotuloPrazo } from '@/hooks/useSla';

/* A TRAVA DA BKO PORTABILIDADE (Douglas 24/10/2026, "não quero travar elas, mas
   se elas não fizerem no tempo que tem que ser feito vai travar"): cada pedido
   tem o seu prazo (sla_filas) e a trava só aparece quando algum estoura, só com
   os estourados. O PN continua só saindo da Inspeção depois que ela fala com o
   cliente (regra do banco). No SMS, cada cliente é cobrado uma vez por dia
   (23/10). O Inbox continua aberto: lá a trava vira faixa. Quem decide é
   fn_bko_portabilidade_travado; o painel só abre as triagens e conversas que já
   existem. */

type Fila = 'inspecao' | 'apresentacao' | 'agendada' | 'sms' | 'bilhete' | 'divergente' | 'complementar';
type PedidoTrava = { pedido_id: string; numero_pedido: number | null; cliente: string | null; conversa_id: string | null; batido: boolean; estourou_min?: number };
type PedidoCobrado = { pedido_id: string; numero_pedido: number | null; cliente: string | null; hora: string | null };
type Trava = { aplica: boolean; travado: boolean; fila?: Fila | null; hora?: string | null; pedidos_trava?: PedidoTrava[]; cobrados_hoje?: PedidoCobrado[]; travas_hoje?: number };

const INSPECAO = {
  nome: 'Inspeção concluída', regra: '2 h sem tratar',
  destrava: 'Destrava: o pedido andar (SMS Enviado, Aguardar NF, CPF/CNPJ Divergente, Finalizar ou cancelar). O PN só sai depois da sua mensagem ao cliente.',
  triagem: 'Triagem Inspeção concluída', conversa: false,
};

const FILAS: Record<Fila, { nome: string; regra: string; destrava: string; triagem: string; conversa: boolean }> = {
  inspecao: INSPECAO,
  apresentacao: INSPECAO,
  bilhete: {
    nome: 'Nota fiscal / Bilhete', regra: '4 h sem consulta',
    destrava: 'Destrava: o bilhete saiu (SMS Enviado), Divergente, Complementar, Finalizar, ou Manter com a justificativa do que você viu no sistema.',
    triagem: 'Triagem Nota Fiscal / Bilhete', conversa: false,
  },
  sms: {
    nome: 'SMS enviado sem data', regra: '2 dias úteis',
    destrava: 'Destrava: agendar a data, ou falar com o cliente pela conversa do pedido sobre o SMS do 7678. Justificar sozinho não destrava. Depois de falar, o pedido só volta amanhã.',
    triagem: 'Triagem SMS enviado', conversa: true,
  },
  agendada: {
    nome: 'Portabilidade agendada', regra: '2 h depois da hora marcada',
    destrava: 'Destrava: (PN) Concluído, Reagendar com a data nova ou (PN) Negada.',
    triagem: 'Triagem Portabilidade agendada', conversa: false,
  },
  divergente: {
    nome: 'CPF / CNPJ divergente', regra: '4 h sem tratativa',
    destrava: 'Destrava: Resolver, cancelar por titularidade, pedir o titular ao cliente, Chamado IW ou a tratativa.',
    triagem: 'Triagem CPF / CNPJ divergente', conversa: false,
  },
  complementar: {
    nome: 'Pedido complementar', regra: '4 h sem consulta',
    destrava: 'Destrava: o complemento concluído (volta para a Inspeção), cancelar, ou Manter com a justificativa e o número do chamado.',
    triagem: 'Triagem Pedido Complementar', conversa: false,
  },
};

const REGUA = 'Inspeção: 2 h · Bilhete, Divergente e Complementar: 4 h · SMS sem data: 2 dias úteis, 1 cobrança por dia · Agendada: 2 h depois da hora';

const primeiroNome = (nome: string | null | undefined) => {
  const p = (nome ?? '').split(' ')[0] || '';
  return p.charAt(0) + p.slice(1).toLowerCase();
};

export function BkoPortabilidadeTrava() {
  const { data: perfil } = useUserProfile();
  const ehPortabilidade = perfil?.funcao === 'BKO_PORTABILIDADE';
  const [aberta, setAberta] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['bko-portabilidade-travado', perfil?.id],
    enabled: ehPortabilidade,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Trava> => {
      const { data, error } = await rpcTriagem('fn_bko_portabilidade_travado', {});
      if (error) throw new Error(error.message || 'Não deu para conferir a trava da Portabilidade.');
      return data as Trava;
    },
  });
  if (!ehPortabilidade || !q.data?.travado || !q.data.fila) return null;
  const t = q.data;
  const fila = FILAS[t.fila];
  const pedidos = t.pedidos_trava ?? [];
  const cobrados = t.fila === 'sms' ? t.cobrados_hoje ?? [] : [];
  const faltam = pedidos.filter((p) => !p.batido).length;
  const fechar = () => { setAberta(false); q.refetch(); };
  const abrirConversa = (id: string | null) => { if (id) navigate(`/whatsapp-inbox?conversation=${id}`); };
  const abrir = (p: PedidoTrava) => (fila.conversa && p.conversa_id ? abrirConversa(p.conversa_id) : setAberta(true));

  const modal = <>
    {(t.fila === 'inspecao' || t.fila === 'apresentacao') && <TriagemAtivacaoModal isOpen={aberta} onClose={fechar} />}
    {t.fila === 'sms' && <TriagemPortabilidadeModal isOpen={aberta} onClose={fechar} />}
    {t.fila === 'agendada' && <TriagemPortabilidadeConcluidaModal isOpen={aberta} onClose={fechar} />}
    {t.fila === 'divergente' && <TriagemCpfDivergenteModal isOpen={aberta} onClose={fechar} />}
    {t.fila === 'bilhete' && <TriagemNotaFiscalModal isOpen={aberta} onClose={fechar} />}
    {t.fila === 'complementar' && <TriagemFluxoModal config={TRIAGENS_FLUXO['PEDIDO COMPLEMENTAR']} isOpen={aberta} onClose={fechar} />}
  </>;

  if (pathname.startsWith('/whatsapp-inbox')) {
    const proximo = pedidos.find((p) => !p.batido);
    return (
      <div role="status" className="fixed left-1/2 top-3 z-[9000] flex w-[min(94vw,720px)] -translate-x-1/2 flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] px-4 py-2 text-white shadow-lg">
        <Lock className="h-4 w-4 shrink-0" />
        <p className="min-w-0 flex-1 text-[12.5px]"><span className="font-bold">Trava das {t.hora}:</span> {fila.nome} · faltam <span className="font-bold tabular-nums">{faltam}</span> de {pedidos.length}</p>
        {fila.conversa && proximo?.conversa_id && (
          <button type="button" onClick={() => abrirConversa(proximo.conversa_id)}
            className="rounded-md border border-white/40 px-2 py-1 text-[11.5px] font-bold hover:bg-white/10">
            Próximo: {proximo.numero_pedido ?? '-'}
          </button>
        )}
        <button type="button" onClick={() => setAberta(true)} className="rounded-md border border-white/40 px-2 py-1 text-[11.5px] font-bold hover:bg-white/10">{fila.triagem}</button>
        {modal}
      </div>
    );
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="trava-portabilidade-titulo"
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-background/95 p-4 backdrop-blur-[2px]">
      <div className="w-full max-w-[560px] overflow-hidden rounded-xl border border-border-warm bg-card shadow-[0_1px_2px_rgba(60,20,30,0.05),0_4px_14px_-6px_rgba(60,20,30,0.08)]">
        <div className="bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] px-6 py-5 text-white">
          <p className="text-[10px] font-bold uppercase tracking-[1.2px] text-white/60">{primeiroNome(perfil?.nome)} · Portabilidade · trava das {t.hora ?? '-'}</p>
          <h2 id="trava-portabilidade-titulo" className="mt-1 font-display text-[22px] font-bold">
            {pedidos.length === 1 ? '1 pedido passou do prazo' : `${pedidos.length} pedidos passaram do prazo`}
          </h2>
          <p className="mt-1 text-[12.5px] text-white/85">{fila.nome} · {fila.regra}. Trate {pedidos.length === 1 ? 'este pedido' : 'estes pedidos'} e a sua fila volta.</p>
          <p className="mt-3 font-display text-[34px] font-bold leading-none tabular-nums">Faltam {faltam} de {pedidos.length}</p>
        </div>
        <div className="border-b border-border px-6 py-3">
          <div className="mb-1 flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Pedidos atrasados</p>
            <p className="text-[11px] text-muted-foreground">Travas hoje: <span className="font-bold tabular-nums text-primary">{t.travas_hoje ?? 0}</span></p>
          </div>
          <div className="max-h-[220px] overflow-y-auto">
            {pedidos.map((p) => (
              <div key={p.pedido_id} className="grid h-8 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 border-t border-border text-[12.5px] first:border-t-0">
                <span className="min-w-0 truncate">
                  <span className="font-semibold tabular-nums text-foreground">{p.numero_pedido ?? '-'}</span>
                  <span className="text-muted-foreground"> · {p.cliente || '-'}</span>
                </span>
                {p.batido
                  ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success"><Check className="h-3 w-3" /> batido</span>
                  : <span className="text-[11px] font-bold tabular-nums text-danger">{rotuloPrazo(-(p.estourou_min ?? 0))}</span>}
                {!p.batido
                  ? <button type="button" onClick={() => abrir(p)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[11px] font-semibold text-foreground hover:border-primary/50 hover:text-primary">
                      {fila.conversa ? <><MessageCircle className="h-3 w-3" /> Conversa</> : 'Abrir'}
                    </button>
                  : <span />}
              </div>
            ))}
          </div>
          {cobrados.length > 0 && (
            <div className="mt-2">
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Já cobrados hoje · voltam amanhã às 9h</p>
              <div className="max-h-[132px] overflow-y-auto">
                {cobrados.map((p) => (
                  <div key={p.pedido_id} className="flex h-7 items-center justify-between gap-3 border-t border-border text-[12px] text-muted-foreground">
                    <span className="min-w-0 truncate">
                      <span className="font-semibold tabular-nums">{p.numero_pedido ?? '-'}</span>
                      <span> · {p.cliente || '-'}</span>
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-success"><Check className="h-3 w-3" /> falou às {p.hora ?? '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="space-y-3 px-6 py-4">
          <button type="button" onClick={() => setAberta(true)}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[linear-gradient(135deg,#4A1320,#5B0A0D)] text-[13px] font-bold text-white transition hover:brightness-110 dark:bg-[linear-gradient(135deg,#9D2530,#6C2236)]">
            {fila.triagem}
          </button>
          <p className="text-[12px] text-muted-foreground">{fila.destrava} O resto do CRM fecha até tratar estes; o Inbox continua aberto.</p>
          <p className="text-[11px] text-muted-foreground">{REGUA}</p>
        </div>
      </div>
      {modal}
    </div>
  );
}
